import { COLOURS_SORT_ORDER, SIZES_SORT_ORDER } from '@srcDS/components/organisms/productActions/utils/sortOrder';
import { useCategoryContext } from '@srcDS/components/templates/category/context/useCategoryContext';
import { useCallback, useEffect, useState } from 'react';
import { initialFilterDisplayValues } from '@srcDS//components/templates/category/_vars';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import qs from 'qs';
import { usePageLoading } from '@frontastic/catwalk/js/helper/hooks/usePageLoading';

const sortDataForFacets = {
  'variants.attributes.refinementcolour': COLOURS_SORT_ORDER,
  'variants.attributes.size': SIZES_SORT_ORDER,
};

export const useFilter = () => {
  const {
    node,
    facets,
    streamId,
    stream,
    setMergedFacets,
    setSelectedFilters,
    setTriggerFilter,
    getSelectedFilters,
    setSelectedFacets,
    getSelectedFacets,
    filterCount,
    localFilters,
    setLocalFilters,
  } = useCategoryContext() as any;

  const [filterDisplayValues, setFilterDisplayValues] = useState(initialFilterDisplayValues);

  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const { dispatchPageLoadingStart } = usePageLoading();

  useEffect(() => {
    if (!stream?.facets?.length || !facets?.data?.length) {
      setMergedFacets([]);
    } else {
      // merging facets from stream and props
      const map = new Map();
      stream.facets.forEach((item) => map.set(item.handle, item));
      facets.data.forEach((item) => {
        map.set(item.attributeId, { ...map.get(item.attributeId), ...item });
        if (node.nodeType === 'search') {
          map.set('category.id', {
            attributeId: 'category.id',
            label: { 'en_AU@AUD': 'Category', 'en_AU@NZD': 'Category' },
            type: 'term',
            ...map.get('category.id'),
          });
        }
      });

      const facetsArray = Array.from(map.values());
      const sortedFacetsArray = facetsArray.sort(
        (a, b) => (a.sort ?? Number.MAX_SAFE_INTEGER) - (b.sort ?? Number.MAX_SAFE_INTEGER),
      );
      const sortedFacets = sortedFacetsArray.map((f) => ({
        ...f,
        terms: sortFacetTerms(f),
      }));

      setMergedFacets(sortedFacets.filter((f) => f?.attributeId));
      setSelectedFilters(getSelectedFilters(stream?.facets));
      setTriggerFilter(!!getSelectedFacets());
      setSelectedFacets(getSelectedFacets());

      const initialLocalFilters = sortedFacets
        ?.filter((facet) => facet.type === 'term' && facet.terms?.some((term) => term.selected) && facet?.attributeId)
        .reduce((acc, facet) => {
          acc[facet.identifier || facet.attributeId] = {
            ...facet,
            terms: facet.terms.map((term) => ({
              ...term,
            })),
          };
          return acc;
        }, {});
      const priceRange = sortedFacets.find((filter) => filter.attributeId === 'variants.price');

      if (priceRange.selected) {
        initialLocalFilters['variants.price'] = priceRange;
      }

      setLocalFilters(initialLocalFilters);
    }
  }, [stream?.facets, facets?.data]);

  const parseSearchParamsWithStreamConfig = () => {
    const parsedSearchParams = qs.parse(searchParams.toString());

    if (!parsedSearchParams.s) {
      parsedSearchParams.s = {};
    }
    if (!parsedSearchParams.s[streamId]) {
      parsedSearchParams.s[streamId] = {};
    }
    return parsedSearchParams;
  };

  const onPriceRangeChange = async (facet, min, max) => {
    const newFacet = { ...facet };
    newFacet.min = min;
    newFacet.max = max;

    setLocalFilters((localFilters) => ({
      ...localFilters,
      [facet.attributeId]: [...[...(localFilters?.attrId || [])], newFacet],
    }));
  };

  const handleSortChange = (sort) => {
    const parsedSearchParams = parseSearchParamsWithStreamConfig();

    parsedSearchParams.s[streamId].offset = undefined;

    const { sortAttributes } = parsedSearchParams.s[streamId];

    if (
      sortAttributes !== undefined &&
      Array.isArray(sortAttributes) &&
      sortAttributes.length > 0 &&
      sortAttributes.filter((attr) => Object.hasOwn(attr, sort.attributeId) && attr[sort.attributeId] === sort.order)
        .length > 0
    ) {
      parsedSearchParams.s[streamId].sortAttributes = undefined;
    } else {
      parsedSearchParams.s[streamId].sortAttributes = [{ [sort.attributeId]: sort.order }];
    }

    dispatchPageLoadingStart(false);
    router.push(
      `${pathname}?${qs.stringify(parsedSearchParams, {
        arrayFormat: 'indices',
      })}`,
    );

    document.getElementById('categoryPage')?.parentElement?.scrollIntoView({
      behavior: 'smooth',
      inline: 'end',
    });
  };

  const handleFacetChange = useCallback((facet, status, termIndex, attrId) => {
    setLocalFilters((prevFilters) => {
      const updatedFilters = { ...prevFilters };

      if (!updatedFilters[attrId]) {
        updatedFilters[attrId] = { ...facet, terms: facet.terms.map((term) => ({ ...term, selected: false })) };
      }

      const terms = updatedFilters[attrId].terms;
      if (termIndex >= 0 && termIndex < terms.length) {
        terms[termIndex] = {
          ...terms[termIndex],
          selected: status,
        };
      }

      return { ...updatedFilters };
    });
  }, []);

  const handleTagRemoval = (attrId, termValue) => {
    setLocalFilters((localFilters) => {
      let updatedFilters = { ...localFilters };

      if (attrId === 'variants.price' && updatedFilters[attrId]) {
        const { ['variants.price']: _, ...newObj } = updatedFilters;
        updatedFilters = { ...newObj };
      } else if (!updatedFilters[attrId] || !Array.isArray(updatedFilters[attrId].terms)) {
        return updatedFilters;
      } else {
        const terms = updatedFilters[attrId].terms;
        const termIndex = terms.findIndex((term) => term.name === termValue);

        if (termIndex >= 0) {
          updatedFilters[attrId].terms[termIndex] = {
            ...terms[termIndex],
            selected: false,
          };
        }
      }
      handleSubmitFaceChange({ updatedLocalFilters: updatedFilters });
      return updatedFilters;
    });
  };
  
  const handleSubmitFaceChange = ({ callback = null, updatedLocalFilters = null }) => {
    const parsedSearchParams = {
      s: {
        [streamId]: {},
      },
    } as any;

    const finalLocalFilters = updatedLocalFilters || localFilters;

    if (Object.keys(finalLocalFilters).length > 0) {
      Object.keys(finalLocalFilters).forEach((attrId) => {
        if (!parsedSearchParams.s[streamId].facets) {
          parsedSearchParams.s[streamId].facets = {};
        }
        if (!parsedSearchParams.s[streamId].facets[attrId]) {
          parsedSearchParams.s[streamId].facets[attrId] = {};
        }

        if (finalLocalFilters[attrId].terms) {
          parsedSearchParams.s[streamId].facets[attrId].terms = finalLocalFilters[attrId].terms
            .filter((term) => term.selected)
            .map((term) => term.value);
        } else if (attrId === 'variants.price') {
          if (Array.isArray(finalLocalFilters[attrId])) {
            parsedSearchParams.s[streamId].facets[attrId].min = finalLocalFilters[attrId][0].min;
            parsedSearchParams.s[streamId].facets[attrId].max = finalLocalFilters[attrId][0].max;
          } else {
            parsedSearchParams.s[streamId].facets[attrId].min = finalLocalFilters[attrId].minSelected;
            parsedSearchParams.s[streamId].facets[attrId].max = finalLocalFilters[attrId].maxSelected;
          }
        }
      });
    } else {
      handleClearAllFacets();
      return;
    }

    // @ts-ignore
    parsedSearchParams.s[streamId].offset = undefined;

    dispatchPageLoadingStart(false);
    router.push(
      `${pathname}?${qs.stringify(parsedSearchParams, {
        arrayFormat: 'indices',
      })}`,
    );

    if (callback && typeof callback === 'function') {
      callback();
    }
  };

  const handleClearAllFacets = () => {
    const parsedSearchParams = parseSearchParamsWithStreamConfig();

    parsedSearchParams.s[streamId].facets = {};
    parsedSearchParams.s[streamId].offset = undefined;
    setLocalFilters({});

    dispatchPageLoadingStart(false);
    router.push(
      `${pathname}?${qs.stringify(parsedSearchParams, {
        arrayFormat: 'indices',
      })}`,
    );
  };

  const sortFacetTerms = ({ terms, attributeId }) => {
    if (!attributeId || attributeId === 'variants.price') return terms;
    return terms
      ? [...terms].sort((a, b) => {
          return sortDataForFacets[attributeId]
            ? sortDataForFacets[attributeId].indexOf(a.value) - sortDataForFacets[attributeId].indexOf(b.value)
            : a.value - b.value;
        })
      : undefined;
  };

  const isAnySelected = Object.keys(localFilters)?.some((key) => localFilters[key]?.terms?.some(term => term.selected))

  return {
    sortFacetTerms,
    handleClearAllFacets,
    handleFacetChange,
    handleSubmitFaceChange,
    onPriceRangeChange,
    handleSortChange,
    filterDisplayValues,
    setFilterDisplayValues,
    filterCount,
    localFilters,
    setLocalFilters,
    handleTagRemoval,
    isAnySelected,
  };
};
